import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Smartphone, X } from "lucide-react";

/**
 * PWA Install button.
 * Listens to the browser's `beforeinstallprompt` event, stashes the prompt, and exposes
 * a clean CTA. Hides itself if the app is already installed (display-mode: standalone).
 *
 * Props:
 *   - variant: "hero" (large gold pill) | "compact" (small icon button) | "banner" (full-width)
 *   - className: extra Tailwind classes
 */
export default function InstallPWAButton({ variant = "hero", className = "" }) {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstalled, setIsInstalled] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [showIosTip, setShowIosTip] = useState(false);
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem("gpm_pwa_dismissed") === "1"; } catch (e) { return false; }
    });

    useEffect(() => {
        // Detect already-installed (standalone display mode)
        const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
        if (standalone) {
            setIsInstalled(true);
            return;
        }

        // Detect iOS Safari (which doesn't fire beforeinstallprompt — show manual tip)
        const ua = window.navigator.userAgent.toLowerCase();
        const iosLike = /iphone|ipad|ipod/.test(ua) && !/crios|fxios/.test(ua);
        setIsIOS(iosLike);

        const onBIP = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => {
            setIsInstalled(true);
            setDeferredPrompt(null);
        };
        window.addEventListener("beforeinstallprompt", onBIP);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBIP);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const onInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === "accepted") setIsInstalled(true);
            setDeferredPrompt(null);
            return;
        }
        // No native prompt available (iOS Safari, repeat visit, dismissed, etc.) — show manual tip
        setShowIosTip(true);
    };

    const onDismiss = () => {
        try { localStorage.setItem("gpm_pwa_dismissed", "1"); } catch { /* ignore */ }
        setDismissed(true);
    };

    if (isInstalled) return null;
    // Compact + hero buttons are ALWAYS visible when not installed. Only the top banner
    // respects the persistent dismissed flag — so users can still install from the
    // navbar / mobile hero even after dismissing the banner.
    if (dismissed && variant === "banner") return null;

    if (variant === "banner") {
        return (
            <>
                <div className={`text-on-image relative w-full bg-gradient-to-r from-navy-900 via-navy-700 to-navy-900 text-white px-4 py-3 flex items-center justify-center gap-3 shadow-md ${className}`} data-testid="pwa-banner">
                    <Smartphone className="w-5 h-5 text-gold-500 shrink-0" />
                    <span className="text-sm sm:text-base">Install Global Property Mart for faster access & offline support</span>
                    <button onClick={onInstall} data-testid="pwa-install-btn-banner" className="px-4 py-1.5 rounded-md bg-gold-500 text-navy-900 font-semibold text-sm hover:bg-gold-100 whitespace-nowrap">
                        Install App
                    </button>
                    <button onClick={onDismiss} aria-label="Dismiss" className="text-white/60 hover:text-white p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {showIosTip && <IosTipModal onClose={() => setShowIosTip(false)} onInstallNow={deferredPrompt ? onInstall : null} />}
            </>
        );
    }
    if (variant === "compact") {
        return (
            <>
                <button onClick={onInstall} data-testid="pwa-install-btn-compact" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gold-500/40 text-gold-700 text-xs font-semibold hover:bg-gold-500/10 ${className}`}>
                    <Download className="w-3.5 h-3.5" /> Install App
                </button>
                {showIosTip && <IosTipModal onClose={() => setShowIosTip(false)} onInstallNow={deferredPrompt ? onInstall : null} />}
            </>
        );
    }

    // hero / default
    return (
        <>
            <button
                onClick={onInstall}
                data-testid="pwa-install-btn"
                className={`group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gold-500 text-navy-900 font-semibold shadow-lg shadow-gold-500/25 hover:bg-gold-100 hover:shadow-xl hover:shadow-gold-500/40 transition-all ${className}`}
            >
                <Download className="w-4 h-4 transition-transform group-hover:-translate-y-0.5" />
                Install App
                <span className="ml-1 text-[10px] uppercase tracking-wider opacity-70 group-hover:opacity-100">Free</span>
            </button>
            {showIosTip && <IosTipModal onClose={() => setShowIosTip(false)} onInstallNow={deferredPrompt ? onInstall : null} />}
        </>
    );
}

/* Manual install instructions — used when the browser doesn't expose a native prompt (iOS Safari, repeat visits, dismissed Chrome state, Firefox, Safari macOS, etc.) */
function IosTipModal({ onClose, onInstallNow = null }) {
    // Detect platform for tailored instructions
    const ua = typeof navigator !== "undefined" ? navigator.userAgent.toLowerCase() : "";
    const isIOS = /iphone|ipad|ipod/.test(ua) && !/crios|fxios/.test(ua);
    const isAndroid = /android/.test(ua);

    const heading = isIOS ? "Install on iPhone / iPad" : isAndroid ? "Install on Android" : "Install Global Property Mart";
    const subheading = isIOS
        ? "Tap Share, then Add to Home Screen."
        : isAndroid
            ? "Tap the ⋮ menu in Chrome, then Install app."
            : "Use Chrome or Edge for one-click install.";
    const steps = isIOS
        ? [
            "Tap the Share icon (square with up-arrow)",
            "Scroll & tap \"Add to Home Screen\"",
            "Tap \"Add\" — done!",
          ]
        : isAndroid
            ? [
                "Tap the ⋮ menu (top-right of Chrome)",
                "Tap \"Install app\" or \"Add to Home screen\"",
                "Tap \"Install\" — done!",
              ]
            : [
                "Look for the install icon (⊕) in the address bar",
                "Or open browser menu → \"Install Global Property Mart\"",
                "Click \"Install\" — app opens like a native app",
              ];

    // Body scroll lock + immediate viewport visibility (no page scroll required)
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        // Ensure modal is instantly in view even if the page was scrolled far down
        window.scrollTo({ top: 0, behavior: "instant" });
        return () => { document.body.style.overflow = prev; };
    }, []);

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
            onClick={onClose}
            data-testid="pwa-install-modal"
        >
            <div
                className="relative w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Sticky header with close */}
                <div className="shrink-0 flex items-center justify-between px-5 pt-4 pb-3 border-b border-navy-500/10">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <Smartphone className="w-6 h-6 text-gold-500 shrink-0" />
                        <div className="min-w-0">
                            <h3 className="font-heading text-base font-bold text-navy-500 leading-tight truncate">{heading}</h3>
                            <p className="text-[11px] text-navy-500/60 leading-snug truncate">{subheading}</p>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="shrink-0 text-navy-500/50 hover:text-navy-500 p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Scrollable content — but designed to fit above the fold on 640×360 mobile */}
                <div className="overflow-y-auto px-5 py-4">
                    {/* Primary action — only shown when native prompt is available (Android Chrome, engagement satisfied) */}
                    {onInstallNow && (
                        <button
                            onClick={() => { onClose(); onInstallNow(); }}
                            data-testid="pwa-install-modal-native"
                            className="w-full mb-4 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-gold-500 to-amber-500 text-navy-900 font-extrabold text-sm shadow-md hover:brightness-105"
                        >
                            <Download className="w-4 h-4" />
                            Install now
                        </button>
                    )}
                    <ol className="space-y-2 text-sm text-navy-500/85">
                        {steps.map((s, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <span className="inline-flex items-center justify-center w-6 h-6 shrink-0 rounded-full bg-gold-500/15 text-gold-700 text-xs font-bold">{i + 1}</span>
                                <span className="leading-snug">{s}</span>
                            </li>
                        ))}
                    </ol>
                </div>

                {/* Sticky footer action */}
                <div className="shrink-0 border-t border-navy-500/10 px-5 py-3 bg-white">
                    <button
                        onClick={onClose}
                        data-testid="pwa-install-modal-close"
                        className="w-full px-4 py-2.5 rounded-md bg-white text-navy-500 font-semibold text-sm border border-navy-500/20 hover:bg-navy-500/5"
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
